import { useEffect, useState } from "react"
import { Flex, Form, InputNumber, Radio as AntdRadio, Select } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { modelSelector } from "store/selectors"
import { Card } from "ui/Card"
import { Text } from "ui/Typography"
import { InfoTooltip } from "ui/Tooltip/InfoTooltip"
import { Radio } from "ui/Radio"
import { SwitchInput } from "ui/Input/SwitchInput"
import { MULTI_DAY_PRICE_ALLOWED, PRICING_TYPES, TRIP_PRICE } from "pagesV2/pro/Experiences/ListingScheduling/utils/constants"
import { spacings } from "themes/variables"
import { useWizardContext } from "../../../WizardContext"
import styles from "./MultiDayPricingModel.module.css"

const BUFFER_OPTIONS = [
  { label: "15 min", value: 15 },
  { label: "30 min", value: 30 },
  { label: "45 min", value: 45 },
  { label: "1 hr", value: 60 },
]

export const MultiDayPricingModel = ({ form }) => {
  const { t } = useTranslation()
  const { isFormInitialized } = useWizardContext()
  const trip = useSelector(modelSelector("trip"))
  const pricingType = Form.useWatch("pricing_type", form) || PRICING_TYPES.PER_PERSON
  const isFlatRate = pricingType === PRICING_TYPES.FLAT_RATE

  const maxGuests = Form.useWatch("max_guests", form) ?? trip?.max_guests
  const guestsIncluded = Form.useWatch(TRIP_PRICE.GUESTS_INCLUDED, form)
  const flatRateValue = Form.useWatch(TRIP_PRICE.FLAT_RATE, form)
  const extraGuestPrice = Form.useWatch(TRIP_PRICE.PRICE_ADULT, form)

  const notIncluded =
    typeof maxGuests === "number" && typeof guestsIncluded === "number"
      ? Math.max(0, maxGuests - guestsIncluded)
      : null

  const showFlatRateSummary =
    notIncluded !== null &&
    typeof flatRateValue === "number" &&
    typeof guestsIncluded === "number"

  // These use dedicated MULTI_DAY_PRICE_ALLOWED keys (not the bare adults_allowed /
  // children_allowed) because those bare names collide with trip-level fields on the
  // shared wizard form — e.g. the Details "child friendly" radio is name="children_allowed",
  // which would otherwise overwrite the price toggle and force children_allowed: true.
  const [adultsAllowed, setAdultsAllowed] = useState(
    () => form.getFieldValue(MULTI_DAY_PRICE_ALLOWED.ADULTS) ?? true
  )
  const [childrenAllowed, setChildrenAllowed] = useState(
    () => form.getFieldValue(MULTI_DAY_PRICE_ALLOWED.CHILDREN) ?? false
  )
  const [infantsAllowed, setInfantsAllowed] = useState(
    () => form.getFieldValue(MULTI_DAY_PRICE_ALLOWED.INFANTS) ?? false
  )
  const [passengersAllowed, setPassengersAllowed] = useState(
    () => form.getFieldValue(MULTI_DAY_PRICE_ALLOWED.PASSENGERS) ?? false
  )

  // On edit, buildDefaultValues seeds these form fields AFTER this component mounts.
  // The toggles' local state was read at mount (often before the trip loaded), so it
  // holds stale defaults while the form holds the server value. Re-sync once the form
  // is initialized, and write the resolved boolean back so the form and toggle never
  // diverge — the payload reads the form field, so a mismatch would send the wrong value.
  useEffect(() => {
    if (!isFormInitialized) return
    const sync = (field, setFn, fallback) => {
      const value = form.getFieldValue(field) ?? fallback
      setFn(value)
      form.setFieldValue(field, value)
    }
    sync(MULTI_DAY_PRICE_ALLOWED.ADULTS, setAdultsAllowed, true)
    sync(MULTI_DAY_PRICE_ALLOWED.CHILDREN, setChildrenAllowed, false)
    sync(MULTI_DAY_PRICE_ALLOWED.INFANTS, setInfantsAllowed, false)
    sync(MULTI_DAY_PRICE_ALLOWED.PASSENGERS, setPassengersAllowed, false)
  }, [isFormInitialized, form])

  const handlePricingTypeChange = (e) => {
    form.setFieldValue("pricing_type", e.target.value)
  }

  const handleSwitchChange = (allowedField, priceField, checked, setFn) => {
    form.setFieldValue(allowedField, checked)
    setFn(checked)
    if (!checked && priceField) form.setFieldValue(priceField, null)
  }

  return (
    <Card title={t("experiences.pricing.private.pricing_model")}>
      <Flex vertical gap={spacings.XL}>
        {/* Pricing type */}
        <Form.Item name="pricing_type" initialValue={PRICING_TYPES.PER_PERSON} className="mb-0">
          <AntdRadio.Group onChange={handlePricingTypeChange}>
            <Radio value={PRICING_TYPES.PER_PERSON} className="mb-xs">
              {t("trip.pricing_types.charge_per_person")}
            </Radio>
            <Radio value={PRICING_TYPES.FLAT_RATE} className="mb-xs">
              {t("trip.pricing_types.charge_flat_rate")}
            </Radio>
          </AntdRadio.Group>
        </Form.Item>

        {/* Buffer Time */}
        <Form.Item
          name={TRIP_PRICE.BUFFER_TIME}
          label={
            <Flex align="center" gap={spacings.XXS}>
              <Text fw={500} size="m">
                {t("experiences.scheduling.sections.length_and_rates.buffer_time")}
              </Text>
              <InfoTooltip
                title={t("experiences.scheduling.sections.length_and_rates.buffer_time_info")}
              />
            </Flex>
          }
          className={styles.bufferField}
        >
          <Select options={BUFFER_OPTIONS} allowClear className={styles.bufferSelect} />
        </Form.Item>

        {isFlatRate ? (
          <Flex vertical gap={spacings.L}>
            {/* Guests included + flat rate */}
            <Flex gap={spacings.M} wrap="wrap">
              <Form.Item
                name={TRIP_PRICE.GUESTS_INCLUDED}
                label={
                  <Flex align="center" gap="small">
                    {t("experiences.pricing.private.guests_included_flat_rate")}
                    <InfoTooltip title={t("experiences.pricing.private.guests_included_flat_rate_tooltip")} />
                  </Flex>
                }
                rules={[{ required: true, message: t("form_validation.cannot_be_blank") }]}
                className="mb-0"
              >
                <InputNumber
                  size="large"
                  min={1}
                  max={maxGuests}
                  addonAfter={maxGuests ? `/${maxGuests}` : undefined}
                  className={styles.numberInput}
                />
              </Form.Item>

              <Form.Item
                name={TRIP_PRICE.FLAT_RATE}
                label={t("experiences.pricing.private.set_flat_base_rate")}
                rules={[{ required: true, message: t("form_validation.cannot_be_blank") }]}
                className="mb-0"
              >
                <InputNumber prefix="$" size="large" min={0} className={styles.numberInput} />
              </Form.Item>
            </Flex>

            {/* Not included guests + extra guest price */}
            <Flex gap={spacings.M} wrap="wrap">
              <Flex vertical gap={spacings.XS}>
                <Flex align="center" gap="small">
                  <Text fw={500} size="m">
                    {t("experiences.pricing.private.not_included_flat_rate")}
                  </Text>
                  <InfoTooltip title={t("experiences.pricing.private.not_included_flat_rate_tooltip")} />
                </Flex>
                <InputNumber
                  size="large"
                  readOnly
                  value={notIncluded === null ? "" : notIncluded}
                  addonAfter={maxGuests ? `/${maxGuests}` : undefined}
                  className={styles.numberInput}
                />
              </Flex>

              <Form.Item
                name={TRIP_PRICE.PRICE_ADULT}
                label={t("experiences.pricing.private.charges_per_additional_guest")}
                rules={notIncluded > 0 ? [{ required: true, message: t("form_validation.cannot_be_blank") }] : []}
                className="mb-0"
              >
                <InputNumber
                  prefix="$"
                  size="large"
                  min={0}
                  disabled={notIncluded === 0 || notIncluded === null}
                  className={styles.numberInput}
                />
              </Form.Item>
            </Flex>

            {/* Flat rate summary */}
            {showFlatRateSummary && (
              <Flex align="flex-start" gap={spacings.XS} className={styles.summaryBox}>
                <InfoCircleOutlined className={styles.summaryIcon} />
                <Text size="m">
                  {t("experiences.pricing.private.flat_rate_summary", {
                    max: maxGuests,
                    included: guestsIncluded,
                    flatRate: flatRateValue?.toFixed(2) ?? "0.00",
                    notIncluded,
                    extraPrice: extraGuestPrice?.toFixed(2) ?? "0.00",
                  })}
                </Text>
              </Flex>
            )}

            {/* Allowed guest types (flat rate - toggles only, no price inputs) */}
            <Flex vertical gap={spacings.XS}>
              <Text fw={500} size="l">
                {t("experiences.pricing.private.guests_types_allowed_title")}
              </Text>
              <SwitchInput
                text={t("experiences.scheduling.sections.length_and_rates.adults")}
                subText={t("experiences.scheduling.sections.length_and_rates.available_by_default")}
                checked={adultsAllowed}
                handleSwitchChange={(checked) =>
                  handleSwitchChange(MULTI_DAY_PRICE_ALLOWED.ADULTS, null, checked, setAdultsAllowed)
                }
              />
              <SwitchInput
                text={t("experiences.scheduling.sections.length_and_rates.children")}
                subText={t("experiences.scheduling.sections.length_and_rates.under_12_years")}
                checked={childrenAllowed}
                handleSwitchChange={(checked) =>
                  handleSwitchChange(MULTI_DAY_PRICE_ALLOWED.CHILDREN, TRIP_PRICE.PRICE_CHILD, checked, setChildrenAllowed)
                }
              />
              <SwitchInput
                text={t("experiences.scheduling.sections.length_and_rates.infants")}
                subText={t("experiences.scheduling.sections.length_and_rates.under_2_years")}
                checked={infantsAllowed}
                handleSwitchChange={(checked) =>
                  handleSwitchChange(MULTI_DAY_PRICE_ALLOWED.INFANTS, TRIP_PRICE.PRICE_INFANT, checked, setInfantsAllowed)
                }
              />
            </Flex>
          </Flex>
        ) : (
          /* Per-person guest types with price inputs */
          <Flex vertical gap={spacings.XS}>
            <Text fw={500} size="m">
              {t("experiences.pricing.private.guests_types_title")}
            </Text>
            <SwitchInput
              text={t("experiences.scheduling.sections.length_and_rates.adults")}
              subText={t("experiences.scheduling.sections.length_and_rates.available_by_default")}
              checked={adultsAllowed}
              handleSwitchChange={(checked) =>
                handleSwitchChange(MULTI_DAY_PRICE_ALLOWED.ADULTS, TRIP_PRICE.PRICE_ADULT, checked, setAdultsAllowed)
              }
              inputFormItemName={TRIP_PRICE.PRICE_ADULT}
            />
            <SwitchInput
              text={t("experiences.scheduling.sections.length_and_rates.children")}
              subText={t("experiences.scheduling.sections.length_and_rates.under_12_years")}
              checked={childrenAllowed}
              handleSwitchChange={(checked) =>
                handleSwitchChange(MULTI_DAY_PRICE_ALLOWED.CHILDREN, TRIP_PRICE.PRICE_CHILD, checked, setChildrenAllowed)
              }
              inputFormItemName={TRIP_PRICE.PRICE_CHILD}
            />
            <SwitchInput
              text={t("experiences.scheduling.sections.length_and_rates.infants")}
              subText={t("experiences.scheduling.sections.length_and_rates.under_2_years")}
              checked={infantsAllowed}
              handleSwitchChange={(checked) =>
                handleSwitchChange(MULTI_DAY_PRICE_ALLOWED.INFANTS, TRIP_PRICE.PRICE_INFANT, checked, setInfantsAllowed)
              }
              inputFormItemName={TRIP_PRICE.PRICE_INFANT}
            />
            <SwitchInput
              text={t("experiences.scheduling.sections.length_and_rates.passengers")}
              infoText={t("experiences.scheduling.sections.length_and_rates.passengers_info")}
              checked={passengersAllowed}
              handleSwitchChange={(checked) =>
                handleSwitchChange(MULTI_DAY_PRICE_ALLOWED.PASSENGERS, TRIP_PRICE.PRICE_PASSENGER, checked, setPassengersAllowed)
              }
              inputFormItemName={TRIP_PRICE.PRICE_PASSENGER}
            />
          </Flex>
        )}
      </Flex>
    </Card>
  )
}

MultiDayPricingModel.propTypes = {
  form: PropTypes.object.isRequired,
}
